import React, { Fragment, useEffect, useState } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import { ScaleLoader } from "react-spinners";
import ModalFrame from "../../../toolkit/modalFrame";
import PrimaryButton from "../../../toolkit/primaryButton";
import ErrorAlert from "../../../toolkit/errorAlert";
import { serviceCaller, ServiceCallerProps } from "../../composition";
import { executePhoneRevalidation } from "../../../utils/fetch";
import { IPhoneRevalidationResult } from "../../../types/services/IPhoneRevalidation";
import s from "./styles.scss";

interface Props extends ServiceCallerProps {
  isOpen: boolean;
  toggle: () => void;
}

const SKIP_REASONS: Record<string, string> = {
  NO_PHONE_NUMBER: "no phone number",
  MEETS_FAILURE_THRESHOLD: "two or more failed text messages to this number",
};

function PhoneRevalidationModal(props: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IPhoneRevalidationResult | null>(null);

  const adminUrl = props.config && props.config.adminServiceUrl;

  useEffect(() => {
    if (!props.isOpen) {
      setResult(null);
      setError(null);
    }
  }, [props.isOpen]);

  const requestRevalidation = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await executePhoneRevalidation(
        adminUrl,
        props.currentCharity.id,
        props.serviceAccessToken
      );
      setResult(response);
    } catch (err) {
      setError(err && err.message ? err.message : "Request failed.");
    } finally {
      setLoading(false);
    }
  };

  const renderConfirm = () => (
    <Fragment>
      <p className={s.label}>
        Request a phone lookup for every contact whose phone number is currently
        marked invalid.
      </p>
      <div className={s.actions}>
        <PrimaryButton onClick={props.toggle} variant="secondary">
          Cancel
        </PrimaryButton>
        <PrimaryButton
          onClick={requestRevalidation}
          disabled={loading || !adminUrl}
        >
          Revalidate all invalid phones
        </PrimaryButton>
      </div>
    </Fragment>
  );

  const renderResult = () => {
    const skipped = Object.entries(result.skipped || {});
    return (
      <Fragment>
        <p className={s.label}>
          {result.requestedDonors > 0
            ? `Lookup requested for ${result.requestedDonors} contact(s). Contacts update when the lookup finishes. A contact is skipped if its phone number changes before then.`
            : "No contacts currently have an invalid phone to revalidate."}
        </p>
        {skipped.length > 0 && (
          <p className={s.note}>
            Skipped:{" "}
            {skipped
              .map(
                ([reason, count]) =>
                  `${count} (${SKIP_REASONS[reason] || reason})`
              )
              .join(", ")}
          </p>
        )}
        <div className={s.actions}>
          <PrimaryButton onClick={props.toggle}>Done</PrimaryButton>
        </div>
      </Fragment>
    );
  };

  return (
    <ModalFrame
      isOpen={props.isOpen}
      toggle={props.toggle}
      size="lg"
      title="Revalidate Invalid Phones"
      subtitle="Current validation state"
    >
      <p className={s.note}>
        A phone lookup confirms that the number exists and is a mobile line. It
        does not guarantee that messages will be delivered. When the lookup is
        valid, the contact is restored as before: text messages turn back on
        unless the contact opted out, and contacts deactivated automatically for
        having no valid contact method are reactivated. Contacts with two or
        more failed text messages to their current number are skipped.
      </p>
      <ErrorAlert
        message={error || ""}
        show={!!error}
        onDismiss={() => setError(null)}
      />
      {loading && (
        <div className={s.centered}>
          <ScaleLoader color={"#1f65b1"} loading={loading} />
        </div>
      )}
      {!loading && (result ? renderResult() : renderConfirm())}
    </ModalFrame>
  );
}

export default serviceCaller(withStyles(s)(PhoneRevalidationModal));
